import { Slide, SlideProps, Typography } from "@mui/material";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import Snackbar, { SnackbarCloseReason } from "@mui/material/Snackbar";
import React, { createContext, ReactNode, useContext } from "react";

interface IToastContext {
	toastError: (message: string) => void;
	toastSuccess: (message: string) => void;
	toastWarning: (message: string) => void;
	toastInfo: (message: string) => void;
}

const ToastContext = createContext<IToastContext | null>({
	toastError: () => {},
	toastSuccess: () => {},
	toastWarning: () => {},
	toastInfo: () => {},
});

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
	return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function SlideTransition(props: SlideProps) {
	return <Slide {...props} direction="down" />;
}

const BOTTOM_MENU_OFFSET = "13vh";

const ToastProvider: React.FC<{ children: ReactNode | ReactNode[] }> = ({ children }) => {
	const [toast, setToast] = React.useState<{
		message: string;
		severity: "success" | "warning" | "success" | "info" | "error";
	} | null>(null);
	const [open, setOpen] = React.useState(false);

	const handleClose = (event: any, reason?: SnackbarCloseReason) => {
		if (reason === "clickaway") {
			return;
		}

		setOpen(false);
	};

	const toastError = (message: string) => {
		setToast({ message, severity: "error" });
		setOpen(true);
	};

	const toastSuccess = (message: string) => {
		setToast({ message, severity: "success" });
		setOpen(true);
	};

	const toastWarning = (message: string) => {
		setToast({ message, severity: "warning" });
		setOpen(true);
	};

	const toastInfo = (message: string) => {
		setToast({ message, severity: "info" });
		setOpen(true);
	};

	return (
		<ToastContext.Provider value={{ toastError, toastSuccess, toastWarning, toastInfo }}>
			{children}
			<Snackbar
				open={open}
				autoHideDuration={6000}
				onClose={handleClose}
				TransitionComponent={SlideTransition}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				sx={{
					marginBottom: BOTTOM_MENU_OFFSET,
				}}
			>
				<Alert
					onClose={handleClose}
					severity={toast?.severity ?? "info"}
					sx={{
						width: "100%",
						padding: "1.5em 1em 1em 1em",
					}}
				>
					<Typography
						variant="body1"
						sx={{
							fontWeight: 300,
						}}
					>
						{toast?.message ?? ""}
					</Typography>
				</Alert>
			</Snackbar>
		</ToastContext.Provider>
	);
};

const useToast = () => {
	const context = useContext(ToastContext);

	if (!context) {
		throw new Error("useToast must be used within a ToastProvider");
	}

	return context;
};

export { ToastProvider, useToast };
export default useToast;
